// Country-specific payroll config (tax forms, regions, tax fields).
// Used by employee setup, tax withholdings modal, and readiness checks.

export type CountryCode = 'CA' | 'US' | 'GB' | 'AU';
export type FieldType = 'select' | 'money' | 'number' | 'text';

export interface HelpLink { label: string; url: string; }

export interface TaxField {
  k: string;
  l: string;
  t: FieldType;
  req?: boolean;
  opts?: readonly string[];
  placeholder?: string;
  help?: string;
  helpLink?: HelpLink;
  validate?: (v: unknown) => boolean;
  errorMsg?: string;
  default?: string;
}

export interface TaxIdConfig {
  label: string;
  short: string;
  placeholder: string;
  validate: (v: unknown) => boolean;
  errorMsg: string;
}

export interface CountryConfig {
  code: CountryCode;
  name: string;
  calculationSource: string;
  taxId: TaxIdConfig;
  taxForm: string;
  regionLabel: string;
  regions: readonly string[];
  taxFields: TaxField[];
}

export interface EmployeeReadinessValues {
  name?: string;
  birth?: string;
  taxId?: string;
  empType?: string;
  startDate?: string;
  method?: string;
  payType?: string;
  rate?: string | number;
  [key: string]: unknown;
}

export interface CountryReadinessResult {
  ready: boolean;
  missing: string[];
  invalid: string[];
}

const PROVINCES_CA: readonly string[] = ["Alberta","British Columbia","Manitoba","New Brunswick","Newfoundland and Labrador","Nova Scotia","Northwest Territories","Nunavut","Ontario","Prince Edward Island","Quebec","Saskatchewan","Yukon"];

const STATES_US: readonly string[] = ["Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","District of Columbia","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming"];

const REGIONS_GB: readonly string[] = ["England","Scotland","Wales","Northern Ireland"];
const REGIONS_AU: readonly string[] = ["Australian Capital Territory","New South Wales","Northern Territory","Queensland","South Australia","Tasmania","Victoria","Western Australia"];

function isFilled(v: unknown): boolean {
  return v !== undefined && v !== null && String(v).trim() !== "";
}

function rangeValidate(min: number, max: number): (v: unknown) => boolean {
  return function(v: unknown) {
    if (!isFilled(v)) return true;
    const n = Number(v);
    return Number.isFinite(n) && n >= min && n <= max;
  };
}

const COUNTRIES: Record<CountryCode, CountryConfig> = {
  CA: {
    code: "CA", name: "Canada", calculationSource: "Payroll calculations use the latest CRA payroll formulas (T4127).",
    taxId: {
      label: "Social insurance number", short: "SIN", placeholder: "XXX-XXX-XXX",
      validate: function(v: unknown) { return /^\d{3}[- ]?\d{3}[- ]?\d{3}$/.test(String(v || "").trim()); },
      errorMsg: "Enter a 9-digit SIN like 123-456-789"
    },
    taxForm: "TD1", regionLabel: "Province", regions: PROVINCES_CA,
    taxFields: [
      { k: "provinceEmp", l: "Province of employment", t: "select", req: true, opts: PROVINCES_CA },
      { k: "federalTD1", l: "Federal TD1 claim amount", t: "money", placeholder: "Example: 16,129", help: "Enter the total from Line 13 of the employee's federal TD1 form. Most employees claim only the basic personal amount ($16,129 for 2025).", helpLink: { label: "View federal TD1 form", url: "https://www.canada.ca/en/revenue-agency/services/forms-publications/td1-personal-tax-credits-returns/td1-forms-pay-received-on-january-1-later.html" }, validate: rangeValidate(0, 999999), errorMsg: "Enter $0 to $999,999" },
      { k: "provincialTD1", l: "Provincial TD1 claim amount", t: "money", placeholder: "Example: 22,323", help: "Enter the total from the employee's provincial TD1 form. Each province has its own (TD1AB for Alberta, TD1ON for Ontario, etc.).", helpLink: { label: "View provincial TD1 forms", url: "https://www.canada.ca/en/revenue-agency/services/forms-publications/td1-personal-tax-credits-returns.html" }, validate: rangeValidate(0, 999999), errorMsg: "Enter $0 to $999,999" },
      { k: "additionalTax", l: "Voluntary extra federal tax per pay (optional)", t: "money", placeholder: "Leave blank if none", help: "Only enter if the employee requested extra withholding on TD1 page 2.", validate: rangeValidate(0, 99999), errorMsg: "Enter $0 to $99,999" },
      { k: "cppExempt", l: "CPP exempt", t: "select", opts: ["No","Yes"], default: "No", help: "Most employees are not exempt. Select Yes only if the employee qualifies for a CPP exemption under CRA rules." },
      { k: "eiExempt", l: "EI exempt", t: "select", opts: ["No","Yes"], default: "No", help: "Most employees are not exempt. Select Yes only if the employee qualifies for an EI exemption under CRA rules." }
    ]
  },
  US: {
    code: "US", name: "United States", calculationSource: "Payroll calculations use the latest IRS Publication 15-T tables.",
    taxId: {
      label: "Social security number", short: "SSN", placeholder: "XXX-XX-XXXX",
      validate: function(v: unknown) { return /^\d{3}[- ]?\d{2}[- ]?\d{4}$/.test(String(v || "").trim()); },
      errorMsg: "Enter a 9-digit SSN like 123-45-6789"
    },
    taxForm: "W-4", regionLabel: "State", regions: STATES_US,
    taxFields: [
      { k: "stateEmp", l: "State of employment", t: "select", req: true, opts: STATES_US, help: "The state where the employee performs most of their work. Determines state income tax withholding rules." },
      { k: "filingStatus", l: "Federal filing status", t: "select", opts: ["Single","Married filing jointly","Married filing separately","Head of household"], help: "From the employee's W-4 Step 1(c)." },
      { k: "allowances", l: "Federal allowances", t: "number", help: "From the employee's W-4 Step 3 (dependents claim). The 2020+ W-4 form replaces classic allowances with a dollar amount in Step 3.", validate: rangeValidate(0, 99), errorMsg: "Enter 0 to 99" },
      { k: "additionalFedTax", l: "Voluntary extra federal withholding per pay (optional)", t: "money", help: "Only if requested by the employee on W-4 Step 4(c). Leave blank otherwise.", validate: rangeValidate(0, 99999), errorMsg: "Enter $0 to $99,999" },
      { k: "stateWithholding", l: "Voluntary extra state withholding per pay (optional)", t: "money", help: "Only if requested by the employee. Leave blank otherwise.", validate: rangeValidate(0, 99999), errorMsg: "Enter $0 to $99,999" },
      { k: "fedExempt", l: "Exempt from federal withholding", t: "select", opts: ["No","Yes"], help: "Set Yes only if the employee wrote Exempt on W-4 Step 4(c). Very rare; usually only zero-tax-liability employees." }
    ]
  },
  GB: {
    code: "GB", name: "United Kingdom", calculationSource: "Payroll calculations use the latest HMRC PAYE rates and thresholds.",
    taxId: {
      label: "National Insurance number", short: "NINO", placeholder: "AB 12 34 56 C",
      validate: function(v: unknown) {
        if (!isFilled(v)) return false;
        const clean = String(v).toUpperCase().replace(/\s/g, "");
        return /^[A-CEGHJ-PR-TW-Z]{1}[A-CEGHJ-NPR-TW-Z]{1}\d{6}[A-D]{1}$/.test(clean);
      },
      errorMsg: "Enter a valid NI number like AB 12 34 56 C"
    },
    taxForm: "P45 / P46", regionLabel: "Region", regions: REGIONS_GB,
    taxFields: [
      { k: "taxCode", l: "Tax code", t: "text", req: true, placeholder: "e.g. 1257L",
        help: "From the employee's P45, P46, or HMRC starter checklist. The default for most new employees is 1257L.",
        validate: function(v: unknown) { return !isFilled(v) || /^\s*\d{2,4}[A-Z]{1,2}\s*$/i.test(String(v)); },
        errorMsg: "Enter a valid UK tax code like 1257L" },
      { k: "niCategory", l: "National Insurance category", t: "select", opts: ["A","B","C","F","H","I","J","L","M","S","V","X","Z"], help: "Most employees are category A. HMRC sets the category based on age, residency, and other factors." },
      { k: "studentLoanPlan", l: "Student loan plan", t: "select", opts: ["None","Plan 1","Plan 2","Plan 4","Postgraduate"], help: "From the employee's starter checklist. Select None if they do not have a UK student loan." },
      { k: "pensionPercent", l: "Pension contribution (%)", t: "number", help: "The employee's percentage contribution to the workplace pension. Auto-enrolment minimum is 5%.", validate: rangeValidate(0, 100), errorMsg: "Enter 0 to 100" }
    ]
  },
  AU: {
    code: "AU", name: "Australia", calculationSource: "Payroll calculations use the latest ATO PAYG schedules.",
    taxId: {
      label: "Tax file number", short: "TFN", placeholder: "XXX XXX XXX",
      validate: function(v: unknown) {
        const digits = String(v || "").replace(/\D/g, "");
        return digits.length === 8 || digits.length === 9;
      },
      errorMsg: "Enter an 8 or 9 digit TFN"
    },
    taxForm: "TFN declaration", regionLabel: "State or territory", regions: REGIONS_AU,
    taxFields: [
      { k: "tfnDeclared", l: "Tax file number provided", t: "select", opts: ["Yes","No, claim exemption","Pending"], help: "From the employee's TFN declaration form. Without a TFN, the highest marginal tax rate applies." },
      { k: "freeThreshold", l: "Claiming tax-free threshold", t: "select", opts: ["Yes","No"], help: "From the employee's TFN declaration. Yes for their main job; No for second jobs (avoids underwithholding)." },
      { k: "medicareExempt", l: "Medicare levy exemption", t: "select", opts: ["None","Half","Full"], help: "Most employees have no exemption. Set Half or Full only if the employee provided a valid Medicare levy variation form." },
      { k: "helpHecs", l: "HELP/HECS debt", t: "select", opts: ["No","Yes"], help: "From the employee's TFN declaration. Yes triggers automatic study loan repayments through PAYG." },
      { k: "superRate", l: "Superannuation rate (%)", t: "number", help: "Employer super guarantee rate. The current statutory minimum is 12% (FY 2025-26). Override only if your award or contract requires more.", validate: rangeValidate(0, 50), errorMsg: "Enter 0 to 50" }
    ]
  }
};

const DEFAULT_COUNTRY: CountryCode = "CA";

export function getCountryConfig(code?: string): CountryConfig {
  if (!code) return COUNTRIES[DEFAULT_COUNTRY];
  const upper = String(code).toUpperCase() as CountryCode;
  return COUNTRIES[upper] || COUNTRIES[DEFAULT_COUNTRY];
}

export function isCountrySupported(code?: string): boolean {
  if (!code) return false;
  return !!COUNTRIES[String(code).toUpperCase() as CountryCode];
}

export function listSupportedCountries(): Array<{ code: CountryCode; name: string }> {
  return Object.values(COUNTRIES).map(function(c) { return { code: c.code, name: c.name }; });
}

export function validateField(field: TaxField, value: unknown): string | null {
  if (field.req && !isFilled(value)) return field.l + " is required";
  if (!isFilled(value)) return null;
  if (field.validate && !field.validate(value)) {
    return field.errorMsg || ("Invalid " + field.l.toLowerCase());
  }
  return null;
}

export function computeReadiness(values: EmployeeReadinessValues, countryCode?: string): CountryReadinessResult {
  const config = getCountryConfig(countryCode);
  const missing: string[] = [];
  const invalid: string[] = [];
  function check(k: string, lab: string) { if (!isFilled(values[k])) missing.push(lab); }
  check("name", "Name");
  check("birth", "Birth date");
  if (!isFilled(values.taxId)) missing.push(config.taxId.label);
  else if (config.taxId.validate && !config.taxId.validate(values.taxId)) invalid.push(config.taxId.label);
  check("empType", "Employment type");
  check("startDate", "Start date");
  check("method", "Payment method");
  check("payType", "Pay type");
  check("rate", "Rate");
  config.taxFields.forEach(function(f) {
    if (f.req && !isFilled(values[f.k])) missing.push(f.l);
    else if (isFilled(values[f.k]) && f.validate && !f.validate(values[f.k])) invalid.push(f.l);
  });
  return { ready: missing.length === 0 && invalid.length === 0, missing, invalid };
}
